"use client";

import { useQuery } from "@tanstack/react-query";
import { compactPair, fullNumber, fullPair, levelPercent, type LevelView } from "@/lib/level";
import { toBaseUnits } from "@/lib/format/amount";
import { PlatformError } from "./client";
import { platformFetch } from "./browser";
import { PAGE_LIMIT_MAX_100 } from "./paginate";

/* Tier progress, session-backed.

   `/incentives/tiers/progress` needs a siteBankrollId, which comes from
   /bankrolls, so this resolves the bankroll first. Any 4xx collapses to "no
   progress" rather than an error — the caller only renders these widgets for a
   signed-in player, and a site with no tier programme is not a fault. */

/* The shape the endpoint actually returns. An earlier version guessed
   `percentage`, `current` and `next`; none of those exist, so progress read 0
   for every player. docs/findings/tier-progress-fields-were-guessed.md.

   `currentValue` and `targetValue` are already-scaled decimal strings — 50, not
   50000000000000000000 — even though `nextTier.wageringRequirement` beside them
   is in base units. */
type Progress = {
  currentTier?: Tier | null;
  nextTier?: Tier | null;
  progressFraction?: number;
  currentValue?: string;
  targetValue?: string;
  /* Whether tier progression can change the player's tier at all: true for a
     hard lock and for a member of an externally managed tier, false for a
     floor lock (the sweep still moves them up), an unlocked member or no tier.
     The progress fields keep their meaning either way, so the bar still shows;
     the lock is drawn beside it. The platform sends it from DP-1085 on; a
     missing field reads as unlocked, as in the whitelabel. */
  locked?: boolean;
};

const numberFrom = (value: unknown) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/* Kept pure so the field mapping is testable without a session. */
export const tierProgressView = (data: Progress | null) => {
  const fraction = numberFrom(data?.progressFraction);
  const current = numberFrom(data?.currentValue);
  const target = numberFrom(data?.targetValue);

  const percent =
    fraction !== null
      ? Math.min(100, Math.max(0, fraction * 100))
      : current !== null && target
        ? Math.min(100, (current / target) * 100)
        : 0;

  return {
    percent,
    /* No unit. `currentValue` and `targetValue` arrive without one, and the
       requirement behind them is not always wagering — on this site five of the
       twelve tiers have no `wageringRequirement` at all and four carry a
       `depositRequirement` beside it. The label used to say "wagered", which is
       wrong for those and unverifiable for the rest.
       docs/findings/xp-is-smartico-points-not-tier-progress.md */
    label:
      current !== null && target !== null
        ? `${current} / ${target}`
        : data?.currentTier
          ? "No progress yet"
          : "No tier programme",
    currentTier: data?.currentTier ?? null,
    nextTier: data?.nextTier ?? null,
    locked: data?.locked === true,
  };
};

/* The same progress in the shape the sidebar's rank widget wants.

   This is the fallback source for that widget: when Smartico is not configured,
   the platform's own tier ladder stands in for its levels. The crests are the
   real thing — `imageUrl` on the tier, which the response has carried all along
   and which the widget used to throw away in favour of two hardcoded gradients.

   Deliberately unit-less, unlike the Smartico source which really is measuring
   XP. See `src/lib/level.ts`. */
export const tierLevelView = (data: Progress | null): LevelView | null => {
  const current = data?.currentTier ?? null;

  if (!current) return null;

  const next = data?.nextTier ?? null;
  const value = numberFrom(data?.currentValue);
  const target = numberFrom(data?.targetValue);

  const fraction = numberFrom(data?.progressFraction);
  const percent =
    fraction !== null
      ? Math.min(100, Math.max(0, fraction * 100))
      : value !== null && target !== null
        ? levelPercent(value, target)
        : 0;

  /* Without both numbers there is no pair to render, so the bar carries the
     percentage instead of an invented fraction. */
  const label =
    value !== null && target !== null
      ? compactPair(value, target)
      : `${Math.round(percent)}%`;

  /* The hover shows the pair in full. No unit: the platform's tiers count
     wagering on some ladders and deposits on others (`computeTierProgress`
     binds to whichever requirement the next tier states), and the response
     does not say which, so the bar states the figure and not a word it
     cannot stand behind. */
  const exact = value !== null && target !== null ? fullPair(value, target) : label;
  const remaining = value !== null && target !== null ? target - value : null;
  const toNext =
    next !== null && remaining !== null && remaining > 0
      ? `${fullNumber(remaining)} to ${next.name}`
      : null;
  const locked = data?.locked === true;
  const spokenName = locked ? `${current.name}, tier locked` : current.name;

  return {
    name: current.name,
    nextName: next?.name ?? null,
    imageUrl: current.imageUrl,
    nextImageUrl: next?.imageUrl ?? null,
    percent,
    label,
    exact,
    toNext,
    locked,
    spoken:
      next === null
        ? `${spokenName}, the top tier`
        : `${spokenName} — ${label}, ${Math.round(percent)}% to ${next.name}`,
  };
};

/* Progress towards one specific tier — the chat gate's "Reach Rookie I to
   unlock chat" block (the kit's chat/unlock-requirement). The progress
   endpoint states the pair against the NEXT tier only, and its values are
   scaled decimals while a tier's own `wageringRequirement` is base units.
   When the gate sits further up the ladder, the next tier is the exchange
   rate — its requirement is stated in both units — and the goal is rescaled
   through it. Without a trustworthy rate the view falls back to the
   next-tier pair rather than invent numbers. */
export const tierUnlockView = (
  data: Progress | null,
  required: Tier | null,
): { percent: number; current: number | null; goal: number | null } => {
  const current = numberFrom(data?.currentValue);
  const target = numberFrom(data?.targetValue);
  const next = data?.nextTier ?? null;

  let goal = target;
  if (required && next && required.id !== next.id && target) {
    const nextRequirement = numberFrom(next.wageringRequirement);
    const requiredRequirement = numberFrom(required.wageringRequirement);
    if (nextRequirement && requiredRequirement) {
      goal = requiredRequirement * (target / nextRequirement);
    }
  }

  const fraction = numberFrom(data?.progressFraction);
  const percent =
    current !== null && goal
      ? Math.min(100, Math.max(0, (current / goal) * 100))
      : fraction !== null && (!required || required.id === next?.id)
        ? Math.min(100, Math.max(0, fraction * 100))
        : 0;

  return { percent, current, goal };
};

export const useTierProgress = (bankrollId: string | undefined, enabled: boolean) => {
  const query = useQuery({
    queryKey: ["incentives", "progress", bankrollId],
    enabled: enabled && Boolean(bankrollId),
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return (
          await platformFetch<{ data: Progress }>("incentives/tiers/progress", {
            siteBankrollId: bankrollId,
          })
        ).data;
      } catch (error) {
        if (error instanceof PlatformError && !error.isTransient && error.kind !== "unauthorized") {
          return null;
        }

        throw error;
      }
    },
  });

  return { ...query, ...tierProgressView(query.data ?? null) };
};

/* The site's bonus catalogue — cashback, rakeback and the rest. Public, so a
   signed-out visitor can see what is on offer before deciding to sign up. */

export type Bonus = {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
  interval: string | null;
  isRecurring: boolean;
  iconUrl: string | null;
  imageUrl: string | null;
  termsAndConditions?: string | null;
  startAt?: string | null;
  endAt?: string | null;
};

export const useBonuses = () =>
  useQuery({
    queryKey: ["incentives", "bonuses"],
    staleTime: 5 * 60_000,
    queryFn: async () =>
      (await platformFetch<{ data: Bonus[] }>("incentives/bonuses", { limit: PAGE_LIMIT_MAX_100 })).data,
  });

export type Rollup = {
  id: string;
  bonusId: string;
  wageringProgress: { targetAmount: string; wageredAmount: string } | null;
};

/* How often a surface drawing a wagering bar refetches the two things that
   move it: the bonus's rollup, and the reward's own tranche. Either can be the
   bar's source, so both are polled together — the whitelabel's cadence, which
   refetches rewards and rollups at this interval on every rewards surface.
   React Query only polls a focused tab, so a backgrounded page costs nothing,
   and surfaces that draw no bar keep their slower background interval. */
export const WAGERING_POLL_MS = 5_000;

/* A rollup states a bonus's wagering target and what has been wagered toward
   it, so it moves with every bet — a surface drawing a wagering bar has to
   poll it or the bar sits still until the page is reloaded. `refetchInterval`
   is opt-in for that reason: the whitelabel polls rollups beside rewards on
   every rewards surface, and nowhere else is worth the traffic. */
export const useRollups = (
  enabled: boolean,
  { refetchInterval }: { refetchInterval?: number } = {},
) =>
  useQuery({
    queryKey: ["incentives", "rollups"],
    enabled,
    refetchInterval,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return (
          await platformFetch<{ data: Rollup[] }>("incentives/rollups", { limit: PAGE_LIMIT_MAX_100 })
        ).data;
      } catch (error) {
        if (error instanceof PlatformError && !error.isTransient && error.kind !== "unauthorized") {
          return [];
        }

        throw error;
      }
    },
  });

export const rollupProgressView = (rollup: Rollup | null) => {
  const progress = rollup?.wageringProgress ?? null;
  const target = toBaseUnits(progress?.targetAmount);
  const wagered = toBaseUnits(progress?.wageredAmount);

  if (!progress || target <= 0n) {
    return { percent: 0, label: "No progress yet", complete: false };
  }

  const complete = wagered >= target;
  const percent = complete ? 100 : Number((wagered * 10_000n) / target) / 100;

  return { percent, label: complete ? "Complete" : `${Math.round(percent)}%`, complete };
};

export type Tier = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  ordinal: number;
  wageringRequirement: string | null;
  depositRequirement: string | null;
  isEnabled: boolean;
};

export const tierLadderView = (tiers: Tier[]) =>
  tiers
    .filter((tier) => tier.isEnabled)
    .filter((tier) => tier.wageringRequirement !== null || tier.depositRequirement !== null)
    .sort((left, right) => left.ordinal - right.ordinal);

export const useTierLadder = (bankrollIds: string[]) =>
  useQuery({
    queryKey: ["incentives", "tiers", bankrollIds],
    enabled: bankrollIds.length > 0,
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const asRepeatedKey =
        bankrollIds.length === 1 ? [bankrollIds[0], bankrollIds[0]] : bankrollIds;

      const tiers = await platformFetch<{ data: Tier[] }>("incentives/tiers", {
        siteBankrollIds: asRepeatedKey,
        limit: PAGE_LIMIT_MAX_100,
      });

      return tierLadderView(tiers.data);
    },
  });

export const useCurrentTier = (enabled: boolean) =>
  useQuery({
    queryKey: ["incentives", "tiers", "current"],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return (await platformFetch<{ data: Tier | null }>("incentives/tiers/current")).data;
      } catch (error) {
        if (error instanceof PlatformError && !error.isTransient && error.kind !== "unauthorized") {
          return null;
        }

        throw error;
      }
    },
  });
